/* eslint-disable no-console */
import { flow, getRoot, types } from "mobx-state-tree";
import { values } from "mobx";
import { ApiStore } from "./ApiStore";

const Organization = types.model({
  id: types.identifier,
  name: types.maybe(types.string)
});

const User = types.compose(
  ApiStore,
  types
    .model({
      id: types.identifier,
      token: types.maybe(types.string),
      username: types.maybe(types.string),
      email: types.maybe(types.string),
      organization: types.maybeNull(Organization),
      is_staff: types.optional(types.boolean, false)
    })
    .actions(self => ({
      changePassword: flow(function* changePassword(model) {
        try {
          const res = yield self.fetch2("/change_password/", {
            method: "POST",
            body: JSON.stringify({
              old_password: model.old_password,
              new_password: model.new_password
            })
          });
          const json = yield res.json();

          if (!json.hasOwnProperty("token")) {
            return [false, json];
          } else {
            window.sessionStorage.setItem("api_token", json.token);
            return [true, json];
          }
        } catch (err) {
          console.error("Failed to change password.\n", err);
        }
      })
    }))
    .views(self => ({
      has_simplified_view() {
        return !self.is_staff;
      }
    }))
);

const AuthStore = types.compose(
  ApiStore,
  types
    .model({
      savedToken: types.maybe(types.string),
      loggingIn: types.optional(types.boolean, false),
      bootstrapping: types.optional(types.boolean, false),
      user: types.maybeNull(types.reference(User)),
      users: types.map(User)
    })
    .actions(self => ({
      login: flow(function* login(username, password) {
        const notifications = getRoot(self).notifications();

        self.loggingIn = true;

        try {
          const res = yield self.fetch2("/login/", {
            method: "POST",
            body: JSON.stringify({
              username: username,
              password: password
            })
          });
          const json = yield res.json();

          if (!json.hasOwnProperty("token")) {
            notifications.clear().then(() => {
              notifications.error("Username and/or password were incorrect.");
            });
          } else {
            notifications.clear().then(() => {
              notifications.success("Successfully logged in.");
            });

            yield self.bootstrap(json.token);
          }
        } catch (err) {
          notifications.clear().then(() => {
            notifications.error("Unknown error occured.");
          });
          console.error("Request to log in could not be completed.", err);
        }

        self.loggingIn = false;
      }),

      bootstrap: flow(function* bootstrap(token) {
        self.bootstrapping = true;

        const userRes = yield self.fetch2("/user/", { token: token });

        if (userRes.status !== 200) {
          yield self.logout();
        } else {
          const userJson = yield userRes.json();

          self.users.put({ ...userJson, token: token });
          self.user = userJson.id;
        }

        yield getRoot(self).bootstrap();

        self.bootstrapping = false;
        yield Promise.resolve();
      }),

      logout: flow(function* logout() {
        self.users.clear();
        self.user = null;
        yield Promise.resolve();
      }),

      getUsers: flow(function* getUsers() {
        const notifications = getRoot(self).notifications();
        try {
          const res = yield self.fetch2("/users/", { token: self.token() });
          const json = yield res.json();
          self.users.clear();
          json.forEach(u => {
            self.users.put({ ...u, token: u.token });
          });
        } catch (err) {
          console.error("Failed to retrieve users list.\n", err);
          notifications.error("User list could not be retrieved.");
        }
      })
    }))
    .views(self => ({
      token() {
        return self.user ? self.user.token : null;
      },

      sortedOrganizations() {
        let organizations = [];
        let orgIds = new Set();
        values(this.users)
          .filter(u => u.organization !== null)
          .map(user => user.organization)
          .flat()
          .forEach(o => {
            if (!orgIds.has(o.id)) {
              orgIds.add(o.id);
              organizations.push(o);
            }
          });
        return organizations.sort((a, b) => a.name.localeCompare(b.name));
      }
    }))
);

export { AuthStore };
